// Fee components (Section 5.2). A deal's commission is the sum of one or more
// components, each with its own basis, rate, payer, split and optional override.

import { round2, sum } from '../money';
import { CommissionInputError } from './errors';
import type { LeaseAnalysis } from './lease';

export type FeeBasis =
  /** % of aggregate lease value */
  | 'pct_lease_value'
  /** months of rent, e.g. 1 = first month's rent */
  | 'months_rent'
  /** % of purchase/sale price */
  | 'pct_price'
  | 'flat'
  /** $ per rentable SF */
  | 'per_sf';

export type Payer = 'landlord' | 'seller' | 'purchaser' | 'tenant' | 'other';

export interface FeeComponent {
  key: string;
  label?: string;
  basis: FeeBasis;
  /** Percent for pct bases (4 = 4%), months for months_rent, dollars for flat and per_sf. */
  rate: number | null;
  payer: Payer;
  /**
   * The agent's share of this component, in percent (70 = the agent keeps 70%, the
   * house/co-broker gets 30%). Blank means the agent keeps 100%.
   */
  splitPct?: number | null;
  /** When set, wins over the calculated amount and is shown as overridden. */
  overrideAmount?: number | null;
}

export interface FeeContext {
  lease?: LeaseAnalysis | null;
  price?: number | null;
  /** SF for per_sf components (defaults to the lease's rentable SF). */
  sf?: number | null;
}

export interface FeeResult {
  key: string;
  label?: string;
  basis: FeeBasis;
  payer: Payer;
  /** What the formula produces, in cents. Null when an override covers a component that can't be calculated. */
  calculated: number | null;
  /** The amount that counts: the override if set, otherwise the calculated amount. */
  amount: number;
  overridden: boolean;
  /** The house / co-broker share: what does NOT go to the agent. */
  splitAmount: number;
  /** The agent's share — what Brett actually earns. */
  net: number;
}

export interface FeeTotals {
  components: FeeResult[];
  gross: number;
  split: number;
  net: number;
}

function label(c: FeeComponent): string {
  return c.label ?? c.key;
}

function requireRate(c: FeeComponent): number {
  if (c.rate === null || c.rate === undefined || !Number.isFinite(c.rate)) {
    const hint = c.basis === 'pct_price' ? ' Sale-side commission rates have no default and must be entered on every deal.' : '';
    throw new CommissionInputError(`"${label(c)}" needs a rate.${hint}`);
  }
  if (c.rate < 0) throw new CommissionInputError(`"${label(c)}" rate cannot be negative.`);
  return c.rate;
}

function requireLease(c: FeeComponent, ctx: FeeContext): LeaseAnalysis {
  if (!ctx.lease) throw new CommissionInputError(`"${label(c)}" is based on lease terms, but the deal has no lease economics yet.`);
  return ctx.lease;
}

/** The formula result for one component, unrounded. */
export function calculateComponent(c: FeeComponent, ctx: FeeContext): number {
  switch (c.basis) {
    case 'pct_lease_value':
      return (requireRate(c) / 100) * requireLease(c, ctx).aggregateLeaseValue;
    case 'months_rent': {
      const lease = requireLease(c, ctx);
      if (lease.commissionMonthlyRent === null) {
        throw new CommissionInputError(`"${label(c)}" uses first month's rent, but every month of the term is abated.`);
      }
      return requireRate(c) * lease.commissionMonthlyRent;
    }
    case 'pct_price': {
      const rate = requireRate(c);
      if (!ctx.price || ctx.price <= 0) throw new CommissionInputError(`"${label(c)}" needs a purchase/sale price.`);
      return (rate / 100) * ctx.price;
    }
    case 'flat':
      return requireRate(c);
    case 'per_sf': {
      const sf = ctx.sf ?? ctx.lease?.rentableSf ?? null;
      if (!sf || sf <= 0) throw new CommissionInputError(`"${label(c)}" needs a square footage.`);
      return requireRate(c) * sf;
    }
  }
}

export function calculateFees(components: FeeComponent[], ctx: FeeContext): FeeTotals {
  const results = components.map((c): FeeResult => {
    const hasOverride = c.overrideAmount !== null && c.overrideAmount !== undefined;
    let calculated: number | null;
    try {
      calculated = round2(calculateComponent(c, ctx));
    } catch (err) {
      // An override lets Brett record a fee even when the inputs are incomplete.
      if (!hasOverride) throw err;
      calculated = null;
    }
    const amount = hasOverride ? round2(c.overrideAmount as number) : (calculated as number);
    const agentPct = c.splitPct ?? 100;
    if (agentPct < 0 || agentPct > 100) throw new CommissionInputError(`"${label(c)}" split must be between 0 and 100%.`);
    const splitAmount = round2((amount * (100 - agentPct)) / 100);
    return {
      key: c.key,
      label: c.label,
      basis: c.basis,
      payer: c.payer,
      calculated,
      amount,
      overridden: hasOverride,
      splitAmount,
      net: round2(amount - splitAmount),
    };
  });
  return {
    components: results,
    gross: round2(sum(results.map((r) => r.amount))),
    split: round2(sum(results.map((r) => r.splitAmount))),
    net: round2(sum(results.map((r) => r.net))),
  };
}
